import React from 'react';
import { Link } from 'react-router-dom';
import { 
  FiUserPlus, FiArrowRight, FiMail, FiUserCheck, FiUserX,
  FiSettings, FiShield
} from 'react-icons/fi';
import PublicPageHeader from '../../../components/common/PublicPageHeader';
import PublicPageFooter from '../../../components/common/PublicPageFooter';
import DocsSidebar from '../../../components/docs/DocsSidebar';

const OrgUsers = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <PublicPageHeader />
      
      <div className="flex">
        <DocsSidebar currentPath="/docs/organization/users" />
        
        <main className="flex-1 max-w-4xl mx-auto px-6 py-12">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400 mb-8">
            <Link to="/docs" className="hover:text-blue-600">Docs</Link>
            <span>/</span>
            <Link to="/docs/organization" className="hover:text-blue-600">Organization</Link>
            <span>/</span>
            <span className="text-gray-900 dark:text-white">User Management</span>
          </nav>

          {/* Header */}
          <div className="mb-12">
            <div className="inline-flex p-2 bg-green-100 dark:bg-green-900/30 rounded-xl mb-4">
              <FiUserPlus className="w-6 h-6 text-green-600" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">User Management</h1>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Invite users, manage roles, and control access within your organization.
            </p>
          </div>

          {/* Inviting Users */}
          <section className="mb-16" id="invite-users">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Inviting Users</h2>
            <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-6">
              <ol className="space-y-4">
                <li className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-sm">1</span>
                  <div>
                    <span className="font-medium text-gray-900 dark:text-white">Go to User Management</span>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Navigate to Organization Settings → Users</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-sm">2</span>
                  <div>
                    <span className="font-medium text-gray-900 dark:text-white">Click "Invite User"</span>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Enter the email address of the person to invite</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-sm">3</span>
                  <div>
                    <span className="font-medium text-gray-900 dark:text-white">Select Role & Department</span>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Choose the user's role and assign to a department</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-sm">4</span>
                  <div>
                    <span className="font-medium text-gray-900 dark:text-white">Send Invitation</span>
                    <p className="text-sm text-gray-600 dark:text-gray-300">The user will receive an email to join your organization</p>
                  </div>
                </li>
              </ol>
            </div>
          </section>

          {/* User Roles */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Available Roles</h2>
            <div className="space-y-3">
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                    <span className="font-medium text-gray-900 dark:text-white">Organization Admin</span>
                  </div>
                  <span className="text-sm text-gray-500 dark:text-gray-400">Full organization access</span>
                </div>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                    <span className="font-medium text-gray-900 dark:text-white">Department Manager</span>
                  </div>
                  <span className="text-sm text-gray-500 dark:text-gray-400">Manages department</span>
                </div>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 rounded-full bg-cyan-500"></div>
                    <span className="font-medium text-gray-900 dark:text-white">Team Lead</span>
                  </div>
                  <span className="text-sm text-gray-500 dark:text-gray-400">Leads a team</span>
                </div>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 rounded-full bg-teal-500"></div>
                    <span className="font-medium text-gray-900 dark:text-white">Employee</span>
                  </div>
                  <span className="text-sm text-gray-500 dark:text-gray-400">Standard member</span>
                </div>
              </div>
            </div>
            <Link to="/docs/admin/roles" className="inline-flex items-center text-blue-600 font-medium mt-4 hover:underline">
              Learn more about role hierarchy →
            </Link>
          </section>

          {/* Managing Users */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Managing Existing Users</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <FiUserCheck className="w-6 h-6 text-blue-600 mb-2" />
                <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Change Role</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">Update a user's role and permissions</p>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <FiSettings className="w-6 h-6 text-purple-600 mb-2" />
                <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Update Department</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">Move users between departments</p>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <FiUserX className="w-6 h-6 text-red-600 mb-2" />
                <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Deactivate User</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">Remove access without deleting account</p>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <FiMail className="w-6 h-6 text-green-600 mb-2" />
                <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Resend Invitation</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">Resend pending invitations</p>
              </div>
            </div>
          </section>

          {/* Navigation */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Related Guides</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <Link
                to="/docs/admin/roles"
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-blue-50 dark:bg-blue-900/30 transition-all"
              >
                <span className="font-medium text-gray-900 dark:text-white">Role Hierarchy</span>
                <FiArrowRight className="text-gray-400 dark:text-gray-500" />
              </Link>
              <Link
                to="/docs/admin/permissions"
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-blue-50 dark:bg-blue-900/30 transition-all"
              >
                <span className="font-medium text-gray-900 dark:text-white">Permission System</span>
                <FiArrowRight className="text-gray-400 dark:text-gray-500" />
              </Link>
            </div>
          </section>

          {/* Footer */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-8">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500 dark:text-gray-400">Last updated: January 27, 2026</div>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-500 dark:text-gray-400">Was this helpful?</span>
                <button className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700">Yes</button>
                <button className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700">No</button>
              </div>
            </div>
          </div>
        </main>
      </div>

      <PublicPageFooter />
    </div>
  );
};

export default OrgUsers;
